import { AMPLITUDE_FILTER_EVENT_ID, AMPLITUDE_SCREEN_TYPES, GTM_EVENT_KEY } from 'constants/amplitude';
import type { Product } from 'constants/searchTypes';
import type { AmplifyProductFilterEvent } from 'types/amplitude';

interface FilterEventParams {
  eventType: AmplifyProductFilterEvent['event'];
  filters: {
    sort?: Record<string, string>;
    selected: {
      singleSelects?: Record<string, string[]>;
      multiSelects?: Record<string, string[]>;
    };
    term?: string;
    page?: number;
  };
  sessionId: string;
  products: {
    totalProductCount?: number;
    list?: Product[];
  };
  userInfo: AmplifyProductFilterEvent['user'];
}

export const getTrackingDataForFilterEvent = ({
  eventType,
  filters,
  sessionId,
  products,
  userInfo
}: FilterEventParams): AmplifyProductFilterEvent => {
  // Convert sort object to array format, default to relevance
  const sortType =
    filters?.sort && Object.keys(filters.sort).length > 0
      ? Object.keys(filters.sort).map(key => `${key}/${filters.sort?.[key]}`)
      : ['relevance/desc'];

  // Combine single and multi-select filters into unified format
  const formattedFilters = [
    ...Object.keys(filters.selected.singleSelects || {}).map(name => ({
      name,
      operator: 'in',
      value: filters.selected.singleSelects?.[name]?.join(', ') || ''
    })),
    ...Object.keys(filters.selected.multiSelects || {}).map(name => ({
      name,
      operator: 'in',
      value: filters.selected.multiSelects?.[name]?.join(', ') || ''
    }))
  ];

  return {
    event: eventType,
    sessionId,
    search_results: products?.totalProductCount || 0,
    results:
      products?.list?.map(result => ({
        sku: result.productId,
        styleId: result.styleId
      })) || [],
    screen: {
      type: AMPLITUDE_SCREEN_TYPES.search,
      name: `${AMPLITUDE_SCREEN_TYPES.search}:${filters?.term || ''}`
    },
    stringResults: {
      results: products?.list || [],
      results_set: (filters?.page || 0) + 1,
      sort_type: sortType,
      filters: formattedFilters
    },
    user: userInfo,
    [GTM_EVENT_KEY]: AMPLITUDE_FILTER_EVENT_ID
  };
};

/**
 * Converts a page type from historyFactory.getType() to an Amplitude screen type
 * @param pageType - The page type from historyFactory (e.g., 'search', 'pdp', 'cart')
 * @returns The corresponding Amplitude screen type, or 'unknown' if no mapping exists
 */
export const getAmplitudePageType = (pageType?: string): string =>
  AMPLITUDE_SCREEN_TYPES[pageType as keyof typeof AMPLITUDE_SCREEN_TYPES] || 'unknown';
